import _ from "lodash";
import { createSelector } from "reselect";

import { ThousandSeparatorFormatNames } from "../../../../constants/dateFormats";
import { immutableRecords, Precision } from "../../../../types";
import calendarTypes from "../../../../types/calendarTypes";
import dateByAddingSpan from "./dateByAddingSpan";
import { calendarPrecalculations } from "./dateInternalCalculations";
import dateSubtractionFunctions from "./dateSubtractionFunctions";
import replaceWithSpaces from "./replaceWithSpaces";

export const timespanForComponents = (years, months, weeks, days, hours, minutes, seconds) =>
  immutableRecords.TimespanRecord({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds
  });

export const timespanForPrecisionAndUnits = (precision, units) => {
  return immutableRecords.TimespanRecord({
    years: precision === Precision.Year ? units : 0,
    months: precision === Precision.Month ? units : 0,
    weeks: precision === Precision.Week ? units : 0,
    days: precision === Precision.Day ? units : 0,
    hours: precision === Precision.Hour ? units : 0,
    minutes: precision === Precision.Minute ? units : 0,
    seconds: precision === Precision.Second ? units : 0
  });
};

export const timespanForPrecision = (precision, number = 1) => {
  return immutableRecords.TimespanRecord({
    years: precision === Precision.Year ? number : 0,
    months: precision === Precision.Month ? number : 0,
    weeks: precision === Precision.Week ? number : 0,
    days: precision === Precision.Day ? number : 0,
    hours: precision === Precision.Hour ? number : 0,
    minutes: precision === Precision.Minute ? number : 0,
    seconds: precision === Precision.Second ? number : 0
  });
};

export const getReverseTimespan = ts => {
  return immutableRecords.TimespanRecord(
    ts
      ? {
        years: -ts.years,
        months: -ts.months,
        weeks: -ts.weeks,
        days: -ts.days,
        hours: -ts.hours,
        minutes: -ts.minutes,
        seconds: -ts.seconds
      }
      : {}
  );
};

export const addTimespans = (ts1, ts2) => {
  const t1 = immutableRecords.TimespanRecord(ts1);
  const t2 = immutableRecords.TimespanRecord(ts2);
  return immutableRecords.TimespanRecord({
    years: t1.years + t2.years,
    months: t1.months + t2.months,
    weeks: t1.weeks + t2.weeks,
    days: t1.days + t2.days,
    hours: t1.hours + t2.hours,
    minutes: t1.minutes + t2.minutes,
    seconds: t1.seconds + t2.seconds
  });
};

export const isTimespanZero = ts =>
  ts.years === 0 &&
  ts.months === 0 &&
  ts.weeks === 0 &&
  ts.days === 0 &&
  ts.hours === 0 &&
  ts.minutes === 0 &&
  ts.seconds === 0;

export const isTimespanNegative = ts =>
  _.isNil(ts) ||
  ts.years < 0 ||
  ts.months < 0 ||
  ts.weeks < 0 ||
  ts.days < 0 ||
  ts.hours < 0 ||
  ts.minutes < 0 ||
  ts.seconds < 0;

export const areTimespansSame = (tsA, tsB) =>
  (!tsA && !tsB) ||
  (tsA &&
    tsB &&
    tsA.years === tsB.years &&
    tsA.months === tsB.months &&
    tsA.weeks === tsB.weeks &&
    tsA.days === tsB.days &&
    tsA.hours === tsB.hours &&
    tsA.minutes === tsB.minutes &&
    tsA.seconds === tsB.seconds);

export const timespanBetweenTimespans = (startDate, tsA, tsB, calendar) => {
  if (startDate && tsA && tsB && calendar) {
    const endA = dateByAddingSpan(calendar, startDate, tsA);
    const endB = dateByAddingSpan(calendar, startDate, tsB);
    return dateSubtractionFunctions.timespanBetweenDates(calendar, endA, endB);
  }
  return null;
};

export const precisionForTimespan = function(timespan) {
  if (timespan.get("seconds")) return Precision.Second;
  if (timespan.get("minutes")) return Precision.Minute;
  if (timespan.get("hours")) return Precision.Hour;
  if (timespan.get("days")) return Precision.Day;
  if (timespan.get("weeks")) return Precision.Week;
  if (timespan.get("months")) return Precision.Month;
  return Precision.Year;
};

export const unitsForTimespan = function(timespan, precision) {
  switch (precision) {
    case Precision.Year:
      return timespan.years;
    case Precision.Month:
      return timespan.months;
    case Precision.Week:
      return timespan.weeks;
    case Precision.Day:
      return timespan.days;
    case Precision.Hour:
      return timespan.hours;
    case Precision.Minute:
      return timespan.minutes;
    case Precision.Second:
      return timespan.seconds;
    default:
      return null;
  }
};

export const stringFromTimespan = function(timespan, componentLimit, shortFormat) {
  const limit = componentLimit ? componentLimit : 8;
  let count = 0;
  let result = "";
  if (timespan) {
    if (timespan.years) {
      if (!shortFormat) {
        result += timespan.years + " year" + (timespan.years !== 1 ? "s, " : ", ");
      } else {
        result += timespan.years + "y ";
      }
      count++;
    }
    if (timespan.months && count < limit) {
      if (!shortFormat) {
        result += timespan.months + " month" + (timespan.months !== 1 ? "s, " : ", ");
      } else {
        result += timespan.months + "m ";
      }
      count++;
    }
    if (timespan.weeks && count < limit) {
      if (!shortFormat) {
        result += timespan.weeks + " week" + (timespan.weeks !== 1 ? "s, " : ", ");
      } else {
        result += timespan.weeks + "w ";
      }
      count++;
    }
    if (timespan.days && count < limit) {
      if (!shortFormat) {
        result += timespan.days + " day" + (timespan.days !== 1 ? "s, " : ", ");
      } else {
        result += timespan.days + "d ";
      }
      count++;
    }
    if (timespan.hours && count < limit) {
      if (!shortFormat) {
        result += timespan.hours + " hour" + (timespan.hours !== 1 ? "s, " : ", ");
      } else {
        result += timespan.hours + "h ";
      }
      count++;
    }
    if (timespan.minutes && count < limit) {
      if (!shortFormat) {
        result += timespan.minutes + " minute" + (timespan.minutes !== 1 ? "s, " : ", ");
      } else {
        result += timespan.minutes + "m ";
      }
      count++;
    }
    if (timespan.seconds && count < limit) {
      if (!shortFormat) {
        result += timespan.seconds + " second" + (timespan.seconds !== 1 ? "s, " : ", ");
      } else {
        result += timespan.seconds + "s ";
      }
      count++;
    }
  }

  if (count > 0) {
    return result.substr(0, !shortFormat ? result.length - 2 : result.length - 1);
  }
  return !shortFormat ? "0 seconds" : "0s";
};

export const stringFromTimespanRange = function(shortTimespan, longTimespan, componentLimit, shortFormat) {
  const shortString = shortTimespan ? stringFromTimespan(shortTimespan, componentLimit, shortFormat) : "";
  const longString = longTimespan ? stringFromTimespan(longTimespan, componentLimit, shortFormat) : "";
  if (areTimespansSame(shortTimespan, longTimespan)) {
    return shortString;
  }
  if (
    !isTimespanZero(shortTimespan) &&
    shortString.split(",").length === 1 &&
    longString.split(",").length === 1 &&
    shortString.split(" ")[1] === longString.split(" ")[1]
  ) {
    // compress when both have only one unit and the units are same
    const shortSplit = shortString.split(" ");
    const longSplit = longString.split(" ");
    return `${shortSplit[0]} - ${longString.split(" ")[0]} ${longSplit[1].replace(",", "")}`;
  } else {
    return `${shortString ? shortString : ""}${shortString && longString ? " - " : ""}${longString ? longString : ""}`;
  }
};

const absoluteTimeSpanForString = (str, calendar) => {
  const timespanRegEx = timespanRegularExpressions(calendar);

  // For removing thousand seperators (e.g. 1,000,000)
  let thousandSeperatorRegex =
    calendar && calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period ? /\./g : /,/g;

  let years = 0;
  let months = 0;
  let weeks = 0;
  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  let somethingMatched = false;
  let remainingStr = str;

  let array = str.match(timespanRegEx.negativeRegExp);

  let negative = false;
  if (array != null) {
    negative = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.yearsRegExp);
  if (array != null) {
    years = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.monthsRegExp);

  if (array != null) {
    months = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.weeksRegExp);
  if (array != null) {
    weeks = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.daysRegExp);
  if (array != null) {
    days = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.hoursRegExp);
  if (array != null) {
    hours = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.minutesRegExp);
  if (array != null) {
    minutes = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.secondRegExp);
  if (array != null) {
    seconds = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    somethingMatched = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  if (!somethingMatched) {
    return null;
  }

  // Return if there's any extra text besides punctuation
  remainingStr = remainingStr.replace(/,/g, "");
  remainingStr = remainingStr.replace(/\./g, "");
  remainingStr = remainingStr.replace(/\+/g, "");
  const trimmedRemainingStr = remainingStr.trim();

  if (trimmedRemainingStr) {
    return null;
  }

  if (negative == true) {
    years = -years;
    months = -months;
    weeks = -weeks;
    days = -days;
    hours = -hours;
    minutes = -minutes;
    seconds = -seconds;
  }

  return timespanForComponents(years, months, weeks, days, hours, minutes, seconds);
};

const relativeTimeSpanForString = (str, calendar) => {
  const calendarType = calendar ? calendar.calendarType : calendarTypes.CALENDAR_TYPE_DATE_TIME;
  const timespanRegEx = timespanRegularExpressions(calendar);

  // For removing thousand seperators (e.g. 1,000,000)
  let thousandSeperatorRegex =
    calendar && calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period ? /\./g : /,/g;

  let weeks = 0;
  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  let remainingStr = str;

  let array = str.match(timespanRegEx.negativeRegExp);
  let negative = false;
  if (array != null) {
    negative = true;
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.yearsRegExp);
  if (array != null) {
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.monthsRegExp);
  if (array != null) {
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.weeksRegExp);
  if (array != null) {
    if (calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
      weeks = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    }
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.daysRegExp);
  if (array != null) {
    if (calendarType <= calendarTypes.CALENDAR_TYPE_DAY_TIME) {
      days = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    }
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.hoursRegExp);
  if (array != null) {
    hours = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.minutesRegExp);
  if (array != null) {
    minutes = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  array = str.match(timespanRegEx.secondRegExp);
  if (array != null) {
    seconds = parseInt(array[1].replace(thousandSeperatorRegex, ""));
    remainingStr = replaceWithSpaces(remainingStr, array.index, array[0].length);
  }

  // Return if there's any extra text besides punctuation
  remainingStr = remainingStr.replace(/,/, "");
  remainingStr = remainingStr.replace(/\./, "");
  remainingStr = remainingStr.replace(/\+/, "");
  const trimmedRemainingStr = remainingStr.trim();
  if (trimmedRemainingStr && trimmedRemainingStr != "+") {
    return timespanForComponents(0, 0, 0, 0, 0, 0, 0);
  }

  if (negative == true) {
    weeks = -weeks;
    days = -days;
    hours = -hours;
    minutes = -minutes;
    seconds = -seconds;
  }

  return timespanForComponents(0, 0, weeks, days, hours, minutes, seconds);
};

export const timespanForString = function(str, calendar) {
  if (!calendar || calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    return absoluteTimeSpanForString(str, calendar);
  } else {
    return relativeTimeSpanForString(str, calendar);
  }
};

export const roundedTimespanForScale = (calendar, span, unitScale, snapToTimespan) => {
  const precalculations = calendarPrecalculations(calendar);
  const testUnitScale = unitScale;

  if (!snapToTimespan) {
    const secondsCutoff = 30;
    if (testUnitScale < secondsCutoff) {
      const units = unitScale;
      const rounded = units <= 1 ? 1 : units <= 2 ? 2 : units <= 5 ? 5 : units <= 10 ? 10 : units <= 15 ? 15 : 30;
      return span.set("seconds", Math.floor(span.seconds / rounded) * rounded);
    }

    const minutesCutoff = 1800;
    if (testUnitScale < minutesCutoff) {
      const units = unitScale / 60;
      const rounded = units <= 1 ? 1 : units <= 2 ? 2 : units <= 5 ? 5 : units <= 10 ? 10 : units <= 15 ? 15 : 30;
      return span.merge({
        minutes: Math.floor(span.minutes / rounded) * rounded,
        seconds: 0
      });
    }

    const hoursInDay = calendar.hoursInDay;
    const hoursCutoff = (hoursInDay / 2) * 3600;
    if (testUnitScale < hoursCutoff) {
      const units = unitScale / 3600;
      // Round to a factor of hoursInDay (e.g. 24 = 1, 2, 3, 4, 6, 12)
      let rounded = 1;
      for (rounded = 1; rounded < hoursCutoff; rounded++) {
        if (hoursInDay % rounded === 0) {
          if (units < rounded) {
            break;
          }
        }
      }
      return span.merge({
        hours: Math.floor(span.hours / rounded) * rounded,
        minutes: 0,
        seconds: 0
      });
    }

    const weekdayCount = calendar.weekdays.count();
    const daysCutoff = (weekdayCount / 2) * precalculations.secondsInDay;
    if (testUnitScale < daysCutoff) {
      return span.merge({
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }

    const weeksCutoff = 2 * precalculations.secondsInWeek;
    if (testUnitScale < weeksCutoff) {
      return span.merge({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }

    const monthsCutoff = (calendar.months.count() / 2) * precalculations.avgSecondsInMonth;
    if (testUnitScale < monthsCutoff) {
      return span.merge({
        weeks: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }
  } else {
    if (snapToTimespan.seconds > 0) {
      const rounded = snapToTimespan.seconds;
      return span.set("seconds", Math.floor(span.seconds / rounded) * rounded);
    }
    if (snapToTimespan.minutes > 0) {
      const rounded = snapToTimespan.minutes;
      return span.merge({
        minutes: Math.floor(span.minutes / rounded) * rounded,
        seconds: 0
      });
    }
    if (snapToTimespan.hours) {
      const rounded = snapToTimespan.hours;
      return span.merge({
        hours: Math.floor(span.hours / rounded) * rounded,
        minutes: 0,
        seconds: 0
      });
    }
    if (snapToTimespan.days) {
      const rounded = snapToTimespan.days;
      return span.merge({
        days: Math.floor(span.days / rounded) * rounded,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }
    if (snapToTimespan.weeks) {
      const rounded = snapToTimespan.weeks;
      return span.merge({
        weeks: Math.floor(span.weeks / rounded) * rounded,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }
    if (snapToTimespan.months) {
      const rounded = snapToTimespan.months;
      return span.merge({
        months: Math.floor(span.months / rounded) * rounded,
        weeks: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }
    if (snapToTimespan.years) {
      const rounded = snapToTimespan.years;
      return span.merge({
        years: Math.floor(span.years / rounded) * rounded,
        months: 0,
        weeks: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }
  }

  return span.merge({
    months: 0,
    weeks: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
};

// Note: this function is protected by createSelector, so it should only be called if the calendar itself
// changes...
const timespanRegularExpressions = createSelector(
  // select the calendar from what is passed in
  calendar => calendar,
  // And this is the function that calculates what we need...
  calendar => {
    if (calendar && calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period) {
      return {
        yearsRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(y|yr|year)(s)?\\b", "i"),
        monthsRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(m|mo|mnth|mth|month)(s)?\\b", "i"),
        weeksRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(w|wk|week)(s)?\\b", "i"),
        daysRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(d|day)(s)?\\b", "i"),
        hoursRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(h|hr|hour)(s)?\\b", "i"),
        minutesRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(mi|mn|min|minute)(s)?\\b", "i"),
        secondRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\s?(s|sec|second)(s)?\\b", "i"),
        numberRegExp: new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))"),
        negativeRegExp: new RegExp("^\\s*-")
      };
    }

    return {
      yearsRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(y|yr|year)(s)?\\b", "i"),
      monthsRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(m|mo|mnth|mth|month)(s)?\\b", "i"),
      weeksRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(w|wk|week)(s)?\\b", "i"),
      daysRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(d|day)(s)?\\b", "i"),
      hoursRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(h|hr|hour)(s)?\\b", "i"),
      minutesRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(mi|mn|min|minute)(s)?\\b", "i"),
      secondRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\s?(s|sec|second)(s)?\\b", "i"),
      numberRegExp: new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))"),
      negativeRegExp: new RegExp("^\\s*-")
    };
  }
);

export const timespanToApproximateSeconds = (calendar, timespan) => {
  const precalculations = calendarPrecalculations(calendar);

  return (
    timespan.years * precalculations.avgSecondsInYear +
    timespan.months * precalculations.avgSecondsInMonth +
    timespan.weeks * precalculations.secondsInWeek +
    timespan.days * precalculations.secondsInDay +
    timespan.hours * 3600 +
    timespan.minutes * 60 +
    timespan.seconds
  );
};

export const secondsToTimespanInWeeks = (calendar, seconds) => {
  const precalculations = calendarPrecalculations(calendar);
  const weeks = seconds / precalculations.secondsInWeek;
  return timespanForPrecisionAndUnits(Precision.Week, weeks);
};
